import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Star, Briefcase } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { createLocalDate } from "@/utils/dateUtils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ControleProjetoCard } from "@/components/controle-projetos/ControleProjetoCard";
import { useProjectActions } from "@/components/controle-projetos/useProjectActions";
import { DetailSection } from "@/components/freelancers/DetailSection";
import type { ControleProjeto, ControleProjetoAvaliacao as JobEvaluation } from "@/types/controle-projeto";
import { calcularEstadoProjeto, type EstadoProjeto } from "@/utils/controle-projeto-estado";
import { anexarPlanejados } from "@/utils/controle-projeto-planejados";
import { projectArea } from "@/utils/project-area";
import { formatCents, parseMoeda } from "@/utils/money";

interface FreelancerProjetosProps {
  freelancerId: string;
  /** Conteúdo opcional exibido numa coluna à direita (ex.: match com agências). */
  sidebar?: ReactNode;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return format(createLocalDate(value), "dd/MM/yy", { locale: ptBR });
}

function formatValor(valor?: number, moeda?: string | null): string {
  if (!valor) return "—";
  return formatCents(Math.round(valor * 100), parseMoeda(moeda));
}

// Status resumido p/ a tabela, a partir da mesma fonte de verdade do card/kanban
// (calcularEstadoProjeto). "Vencido" = passou da data sem Finalizar nem Renovar.
const ESTADO_BADGE: Record<EstadoProjeto, { label: string; className: string }> = {
  rascunho: { label: "A iniciar", className: "bg-amber-50 text-amber-700 border-amber-200" },
  planejado: { label: "Planejado", className: "bg-sky-50 text-sky-700 border-sky-200" },
  a_iniciar: { label: "A iniciar", className: "bg-amber-50 text-amber-700 border-amber-200" },
  em_andamento: { label: "Ativo", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  fixo: { label: "Ativo", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  vencido: { label: "Vencido", className: "bg-red-50 text-red-700 border-red-200" },
  nao_renova: { label: "Não renova", className: "bg-orange-50 text-orange-700 border-orange-200" },
  renovado: { label: "Renovado", className: "bg-violet-50 text-violet-700 border-violet-200" },
  finalizado: { label: "Finalizado", className: "bg-slate-100 text-slate-600 border-slate-200" },
};

function getProjetoStatus(projeto: ControleProjeto): { label: string; className: string } {
  return ESTADO_BADGE[calcularEstadoProjeto(projeto).estado];
}

/** Nota resumida de uma avaliação (média geral ou rating), ou null se não há. */
function evaluationScore(evaluation?: JobEvaluation): number | null {
  if (!evaluation) return null;
  return evaluation.media_geral ?? evaluation.rating ?? null;
}

function RatingChip({ label, evaluation }: { label: string; evaluation?: JobEvaluation }) {
  const score = evaluationScore(evaluation);
  if (score === null) {
    return (
      <span className="text-xs text-muted-foreground">
        {label} —
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
      <span className="font-medium tabular-nums">{score}</span>
    </span>
  );
}

export function FreelancerProjetos({ freelancerId, sidebar }: FreelancerProjetosProps) {
  const [selectedProjeto, setSelectedProjeto] = useState<ControleProjeto | null>(null);
  const { actions: projectActions, dialogs: projectActionDialogs } = useProjectActions();

  const { data: projetos, isLoading } = useQuery({
    queryKey: ["freelancer-projetos", freelancerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("controle_projetos")
        .select(`
          *,
          agencia:agencias(nome, email),
          freelancer:freelancers(nome, email),
          job_evaluations(*)
        `)
        .eq("freelancer_id", freelancerId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const projetosData = (data || []).map((projeto) => {
        const evaluations = projeto.job_evaluations || [];
        const freelancerEvaluation = evaluations.find(
          (evaluation) => evaluation.evaluator_type === "freelancer",
        );
        const agencyEvaluation = evaluations.find(
          (evaluation) => evaluation.evaluator_type === "agency",
        );

        return {
          ...projeto,
          freelancer_evaluation: freelancerEvaluation,
          agency_evaluation: agencyEvaluation,
        };
      });

      const todos = projetosData as ControleProjeto[];
      return anexarPlanejados(todos, todos.filter((projeto) => projeto.status === "planejado"));
    },
    enabled: !!freelancerId,
  });


  const projetosContent = isLoading ? (
    <div className="space-y-2">
      <Skeleton className="h-10 w-full rounded-md" />
      <Skeleton className="h-10 w-full rounded-md" />
      <Skeleton className="h-10 w-full rounded-md" />
    </div>
  ) : !projetos || projetos.length === 0 ? (
    <p className="text-sm text-muted-foreground">
      Este freelancer ainda não possui projetos cadastrados.
    </p>
  ) : (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]">ID</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Agência</TableHead>
            <TableHead>Área</TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead>Início</TableHead>
            <TableHead>Fim</TableHead>
            <TableHead>Avaliações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {projetos.map((projeto) => (
            <TableRow
              key={projeto.id}
              className="cursor-pointer"
              onClick={() => setSelectedProjeto(projeto)}
            >
              <TableCell className="font-mono text-xs text-muted-foreground">
                #{projeto.id.slice(0, 8)}
              </TableCell>
              <TableCell>
                {(() => {
                  const status = getProjetoStatus(projeto);
                  return (
                    <Badge
                      variant="outline"
                      className={cn("whitespace-nowrap font-normal", status.className)}
                    >
                      {status.label}
                    </Badge>
                  );
                })()}
              </TableCell>
              <TableCell className="max-w-[180px] truncate font-medium">
                {projeto.agencia?.nome || "—"}
              </TableCell>
              <TableCell className="max-w-[160px] truncate">{projectArea(projeto) || "—"}</TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums">
                {formatValor(projeto.valor_projeto, projeto.moeda)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">{formatDate(projeto.data_inicio)}</TableCell>
              <TableCell className="whitespace-nowrap text-sm">
                {projeto.data_inicio && !projeto.data_conclusao ? "Fixo" : formatDate(projeto.data_conclusao)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  <RatingChip label="Frila" evaluation={projeto.freelancer_evaluation} />
                  <RatingChip label="Agência" evaluation={projeto.agency_evaluation} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  // Fecha o dialog de detalhes antes de abrir o da ação: um nível de sobreposição só.
  const closeDetailsThen = <Argument,>(action: (argument: Argument) => void) => (argument: Argument) => {
    setSelectedProjeto(null);
    action(argument);
  };

  return (
    <DetailSection
      title="Projetos"
      icon={Briefcase}
      count={projetos?.length ?? undefined}
    >
      {sidebar ? (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0">{projetosContent}</div>
          <div className="lg:border-l lg:pl-8">{sidebar}</div>
        </div>
      ) : (
        projetosContent
      )}

      {/* Dialog com detalhes completos ao clicar numa linha */}
      <Dialog open={!!selectedProjeto} onOpenChange={(open) => !open && setSelectedProjeto(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do projeto</DialogTitle>
          </DialogHeader>
          {selectedProjeto && (
            <ControleProjetoCard
              projeto={selectedProjeto}
              onRenovar={closeDetailsThen(projectActions.onRenovar)}
              onFinalizar={closeDetailsThen(projectActions.onFinalizar)}
              onConfirmarPlanejado={closeDetailsThen(projectActions.onConfirmarPlanejado)}
              onDesfazerFinalizacao={closeDetailsThen(projectActions.onDesfazerFinalizacao)}
              onExcluir={closeDetailsThen(projectActions.onExcluir)}
            />
          )}
        </DialogContent>
      </Dialog>

      {projectActionDialogs}
    </DetailSection>
  );
}
